import {
  areWindowsEqual,
  DateTrunc,
  DateWindow,
  dateWindowOptions,
  displayStringFromWindow,
  getLabelForWindowPickerOption,
  isDefinedStartWindow,
  isSpecialWindow,
} from '@tableau/metrics-engine';
import { TabStyles } from '@tableau/widgets';
import * as React from 'react';

import { style } from 'typestyle';
import { Messages } from '../../gen-src/Localize';

export interface WindowSwitcherProps {
  /**
   * The date window option to display as selected. Should only be undefined when
   * there is no definition.
   */
  readonly selectedWindow: DateWindow;
  /**
   * The date window options to display.
   */
  readonly options: readonly DateWindow[];
  /**
   * True when all options and their interactivity should be disabled. Currently, the
   * window switcher is only disabled while the user edits the configuration.
   */
  readonly disabled?: boolean;
  /**
   * The id of the div wrapping the timeseries. This is needed for the aria-controls
   * of each option button.
   */
  readonly timeSeriesId: string;
  /**
   * The function to call when a new option is selected.
   */
  readonly onWindowChange: (dateWindow: DateWindow) => void;
}

/**
 * The WindowSwitcher, introduced in 2022.2 with editing, is a component on the detail screen rendered
 * above the timeseries. It allows the user to preview the metric with a different date window without
 * editing the metric configuration.
 *
 * It should be displayed as long as there is more than one data point, but its interactivity should be
 * disabled while the user is editing the configuration so that the "WindowSwitcher selection" and
 * "Configuration selection" are not out of sync. Its options should be updated as the user changes the
 * authored window, and should be re-enabled with the authored window as the selected window when the user
 * is done editing.
 */
export class WindowSwitcher extends React.PureComponent<WindowSwitcherProps, {}> {
  public constructor(props: WindowSwitcherProps) {
    super(props);
  }

  public render(): JSX.Element {
    const { options, selectedWindow } = this.props;
    const selectedOptionIndex = selectedWindow
      ? options.findIndex((dateWindow: DateWindow) => areWindowsEqual(dateWindow, selectedWindow))
      : -1;
    const optionsToDisplay = options.map((dateWindow: DateWindow, i: number) => {
      const isSelected = !this.props.disabled && i === selectedOptionIndex;
      // If for some reason the selectedOptionIndex is -1 (the authoredWindow couldn't be found in options),
      // we don't want any option to be selected (since it could be out of sync with the time series), but
      // we also need the user to be able to tab to the window switcher.
      const shouldBeTabStop = selectedOptionIndex === -1 && i === 0;

      const label = getLabelForWindowPickerOption(dateWindow);
      const ariaLabel =
        isSpecialWindow(dateWindow) || isDefinedStartWindow(dateWindow) ? label : displayStringFromWindow(dateWindow);
      return (
        <React.Fragment key={`option-${i}`}>
          <button
            onClick={() => this.props.onWindowChange(dateWindow)}
            className={OptionStyle}
            data-tb-test-id={`window-switcher-option-${i}${isSelected ? '-selected' : ''}`}
            role={'tab'}
            aria-selected={isSelected}
            tabIndex={isSelected || shouldBeTabStop ? 0 : -1}
            disabled={this.props.disabled}
            aria-label={ariaLabel}
            aria-controls={this.props.timeSeriesId}
          >
            {label}
          </button>
          {i !== options.length - 1 && <span style={SeparatorStyle} />}
        </React.Fragment>
      );
    });

    return (
      <div
        role={'tablist'}
        style={OptionContainerStyle}
        onKeyDown={this.handleKeyDown}
        aria-describedby={'current-legend'}
        aria-label={Messages.ariaWindowSwitcher()}
      >
        {optionsToDisplay}
      </div>
    );
  }

  private handleKeyDown: React.KeyboardEventHandler<HTMLElement> = (event) => {
    const tablist = event.currentTarget;
    const buttonElems = Array.from<HTMLButtonElement>(tablist.querySelectorAll('button:enabled'));
    const currentButtonIndex = buttonElems.indexOf(event.target as HTMLButtonElement);
    const numOptions = buttonElems.length;

    switch (event.key) {
      case 'ArrowRight':
        buttonElems[(currentButtonIndex + 1) % numOptions].focus();
        break;
      case 'ArrowLeft':
        buttonElems[(numOptions + currentButtonIndex - 1) % numOptions].focus();
        break;
      case 'Home':
        buttonElems[0].focus();
        break;
      case 'End':
        buttonElems[numOptions - 1].focus();
        break;
      default:
        return;
    }

    event.preventDefault();
    event.stopPropagation();
  };
}

/**
 * Creates a list of DateWindows that the user may select between on the metric detail page
 * via the WindowSwitcher component. This function is used by MetricDetailView and eventually
 * passed through props to the WindowSwitcher.
 *
 * @param dateTrunc the DateTrunc of the metric
 * @param authoredWindow the window, if it exists, that the metric was authored with
 * @returns an ordered list of DateWindows to be rendered by the WindowSwitcher
 */
export const createWindowSwitcherOptions = (
  dateTrunc: DateTrunc,
  authoredWindow?: DateWindow
): readonly DateWindow[] => {
  const presetOptions = dateWindowOptions(dateTrunc);
  const isAuthoredWindowCustom =
    authoredWindow !== undefined &&
    !presetOptions.some((dateWindow: DateWindow) => areWindowsEqual(dateWindow, authoredWindow));
  return isAuthoredWindowCustom ? [authoredWindow].concat(presetOptions) : presetOptions;
};

// While these styles are very similar to TabsWidget's, that widget can't be used
// because of performance reasons.

const SHADOW_SIZE = 4;
const PADDING_SIZE = 5;

const OptionContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  overflowX: 'auto',
  marginTop: `${TabStyles.Spacing.ExtraLargeSpace}px`,
  // the leftmost option needs to be flush with the BAN/delta text/etc, but it also has
  // margin and padding, so we need negative margin here to keep things aligned
  marginLeft: `-${SHADOW_SIZE + PADDING_SIZE}px`,
  minHeight: '39px',
};

const OptionStyle = style({
  whiteSpace: 'nowrap',
  color: TabStyles.Typography.Color.Primary,
  fontSize: '14px',
  fontFamily: 'inherit',
  fontWeight: 'bold',

  background: 'none',
  borderWidth: '2px 1px 2px 1px',
  borderStyle: 'solid',
  borderColor: 'transparent',
  borderRadius: '2px',
  padding: `${PADDING_SIZE}px`,
  // Margin is needed to keep the focus style shadow to be visible
  // above and below as well
  margin: `${SHADOW_SIZE}px`,

  $nest: {
    '&:focus-visible': {
      border: `1px solid ${TabStyles.Colors.Focus}`,
      boxShadow: `0 0 ${SHADOW_SIZE}px 0 ${TabStyles.Colors.Focus}`,
      outline: 0,
      // plus one to offset the smaller border bottom width
      margin: `${SHADOW_SIZE + 1}px ${SHADOW_SIZE}px`,
    },

    '&:hover:enabled': {
      borderBottom: `2px solid ${TabStyles.Colors.L7}`,
    },

    '&[aria-selected="true"]': {
      color: '#1a699e',
      borderBottom: `2px solid #1a699e`,
      $nest: {
        '&:hover, &:focus-visible': {
          borderBottom: `2px solid #1a699e`,
          marginBottom: `${SHADOW_SIZE}px`,
        },
      },
    },

    '&:disabled': {
      color: TabStyles.Typography.Color.Disabled,
      borderColor: 'transparent',
    },
  },
});

const SeparatorStyle: React.CSSProperties = {
  // 15 is the space between the option text and the separator, but we also
  // need to account for the padding and margin it has
  margin: `0 ${15 - PADDING_SIZE - SHADOW_SIZE}px`,
  borderLeft: `1px solid ${TabStyles.Colors.L3}`,
  height: '24px',
};
